export const GENDER_MALE_COLOR = '#4E9BF6';
export const GENDER_FEMALE_COLOR = '#F9557D';

/** Same fixed bins as Brizi Vision (AgeRange midpoint). */
export const AGE_BUCKETS = [
  '< 25',
  '25 - 34',
  '35 - 44',
  '45 - 54',
  '55 - 64',
  '> 65',
];

export const CROWD_COUNT_AGE_LINE_COLORS = [
  '#EF7E7A',
  '#F2A86B',
  '#AFBD60',
  '#6CC4C1',
  '#7E8ED9',
  '#C7A6E3',
];

export const compareAgeBuckets = (a, b) => {
  const ai = AGE_BUCKETS.indexOf(a);
  const bi = AGE_BUCKETS.indexOf(b);
  const aRank = ai === -1 ? AGE_BUCKETS.length : ai;
  const bRank = bi === -1 ? AGE_BUCKETS.length : bi;
  return aRank - bRank;
};

/**
 * Color for an age bucket. Prefer canonical Vision key; otherwise use
 * fallbackIndex so unknown/legacy keys are still distinct (not all color[0]).
 */
export const getAgeBucketColor = (bucket, fallbackIndex = 0) => {
  const known = AGE_BUCKETS.indexOf(bucket);
  const index = known >= 0 ? known : fallbackIndex;
  return CROWD_COUNT_AGE_LINE_COLORS[index % CROWD_COUNT_AGE_LINE_COLORS.length];
};

/** Fixed 15-min slots for empty-state blur preview. */
const PLACEHOLDER_TIMES = [
  '2026-01-01 11:00:00',
  '2026-01-01 11:15:00',
  '2026-01-01 11:30:00',
  '2026-01-01 11:45:00',
  '2026-01-01 12:00:00',
  '2026-01-01 12:15:00',
  '2026-01-01 12:30:00',
  '2026-01-01 12:45:00',
  '2026-01-01 13:00:00',
  '2026-01-01 13:15:00',
  '2026-01-01 13:30:00',
  '2026-01-01 13:45:00',
];

/** UI-only PRNG step — seed from crypto (not Math.random). */
const jitter = (seed, min, max) => {
  const span = max - min + 1;
  const mixed = Math.abs((seed * 1103515245 + 12345) % 2147483648);
  return min + (mixed % span);
};

const freshSeed = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0] || Date.now();
  }
  return Date.now();
};

/** Age share weights (venue-ish: peak 25–44), jittered per point. */
const AGE_WEIGHTS = [0.12, 0.32, 0.28, 0.14, 0.09, 0.05];

/**
 * Fake crowd-count payload for blurred empty / no-permission preview.
 * New seed every call → different curve each refresh (crypto seed, not Math.random).
 * @param {number} [seed] — optional fixed seed for tests
 */
export const generatePlaceholderCrowdCountData = (seed = freshSeed()) => {
  const floor = jitter(seed, 12, 35);
  const ceiling = jitter(seed + 1, 70, 140);
  let walk = jitter(seed + 2, floor, ceiling);

  const counts = PLACEHOLDER_TIMES.map((_, i) => {
    walk += jitter(seed + i + 3, -28, 28);
    if (jitter(seed + i + 17, 0, 3) === 0) {
      walk += jitter(seed + i + 41, -40, 40);
    }
    walk = Math.max(floor, Math.min(ceiling, walk));
    return walk;
  });

  const countTimeline = PLACEHOLDER_TIMES.map((time, i) => ({
    time,
    count: counts[i],
  }));

  const genderTimeline = PLACEHOLDER_TIMES.map((time, i) => {
    const count = counts[i];
    const share = 0.28 + (jitter(seed + i + 7, 0, 44) / 100);
    const male = Math.max(0, Math.min(count, Math.round(count * share)));
    return { time, male, female: count - male };
  });

  const ageTimeline = AGE_BUCKETS.reduce((acc, bucket) => {
    acc[bucket] = [];
    return acc;
  }, {});

  PLACEHOLDER_TIMES.forEach((x, i) => {
    const weights = AGE_WEIGHTS.map(
      (w, wi) => Math.max(0.01, w + ((jitter(seed + i * 10 + wi + 3, 0, 22) - 11) / 100)),
    );
    const sum = weights.reduce((s, w) => s + w, 0);
    let remaining = counts[i];
    AGE_BUCKETS.forEach((bucket, bucketIndex) => {
      const isLast = bucketIndex === AGE_BUCKETS.length - 1;
      const share = weights[bucketIndex] / sum;
      const y = isLast
        ? Math.max(0, remaining)
        : Math.max(0, Math.round(counts[i] * share + jitter(seed + i + bucketIndex, -4, 4)));
      remaining -= y;
      ageTimeline[bucket].push({ x, y });
    });
  });

  return {
    hasData: true,
    scanCount: PLACEHOLDER_TIMES.length,
    sessionCount: 1,
    countTimeline,
    genderTimeline,
    ageTimeline,
  };
};

